import React, { useCallback, useEffect, useState } from "react";
import { Ban, Plus, Loader2, X, ChevronDown, ChevronUp, Globe, Trash2, MailX, MailWarning } from "lucide-react";
import { toast } from "sonner";
import { api, errText, btnLight, inputCls, fmtDateTime } from "./api";

const REASON = { manual: "added by you", "reply opt-out (auto)": "asked to be removed (reply)", bounced: "bounced" };
const GROUPS = [
  { key: "deleted", title: "Deleted", icon: Trash2, hint: "removed by you — company deletions also block the whole domain" },
  { key: "unsubscribed", title: "Unsubscribed / do-not-contact", icon: MailX, hint: "asked to be removed by reply, or added by you" },
  { key: "bounced", title: "Bounced", icon: MailWarning, hint: "the address rejected our email" },
];

function Group({ g, rows, busy, onUnblock, readOnly }) {
  const [open, setOpen] = useState(false);
  if (!rows.length) return null;
  const shown = open ? rows : rows.slice(0, 5);
  const Icon = g.icon;
  return (
    <div className="border-t border-[#F0EBDC]" data-testid={`blocklist-group-${g.key}`}>
      <div className="px-4 pt-3 pb-1 flex flex-wrap items-baseline gap-x-3">
        <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-[#0F1825]"><Icon size={12} className="text-[#C84C21]" /> {g.title} · {rows.length}</span>
        <span className="text-[11.5px] text-[#A09680]">{g.hint}</span>
      </div>
      <ul>
        {shown.map((s) => (
          <li key={s.email} className="px-4 py-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12.5px]" data-testid={`blocklist-row-${s.email}`}>
            <span className="font-mono text-[12px] text-[#0F1825]">{s.email}</span>
            {(s.name || s.company) && <span className="text-[#0F1825]">{s.name}{s.company ? <span className="text-[#5C6470]"> · {s.company}</span> : null}</span>}
            <span className="text-[#5C6470]">{REASON[s.reason] || s.reason}{s.bounce_reason && g.key === "bounced" ? ` · ${s.bounce_reason}` : ""}{s.text ? ` · “${s.text}”` : ""}</span>
            <span className="ml-auto font-mono text-[10px] text-[#A09680]">{s.added_at ? fmtDateTime(s.added_at) : ""}{s.by && s.by !== "auto" ? ` · ${s.by}` : ""}</span>
            {!readOnly && (
              <button type="button" disabled={!!busy} onClick={() => onUnblock(s.email)} className="font-mono text-[10px] uppercase tracking-[0.16em] text-[#A09680] hover:text-[#0F1825] inline-flex items-center gap-1" data-testid={`unblock-${s.email}`} title="Allow again — restored contacts come back unapproved; nothing sends without fresh approval">
                {busy === s.email ? <Loader2 size={11} className="animate-spin" /> : <X size={11} />} Unblock
              </button>
            )}
          </li>
        ))}
        {rows.length > 5 && (
          <li className="px-4 py-2">
            <button type="button" onClick={() => setOpen((o) => !o)} className="font-mono text-[10px] uppercase tracking-[0.16em] text-[#C84C21] hover:text-[#0F1825] inline-flex items-center gap-1" data-testid={`blocklist-toggle-${g.key}`}>
              {open ? <><ChevronUp size={11} /> Show fewer</> : <><ChevronDown size={11} /> Show all {rows.length}</>}
            </button>
          </li>
        )}
      </ul>
    </div>
  );
}

export function BlocklistPanel({ refreshKey, onChanged, readOnly = false }) {
  const [data, setData] = useState(null);
  const [val, setVal] = useState("");
  const [busy, setBusy] = useState("");
  const load = useCallback(async () => {
    try { setData(await api.blocklist()); } catch (e) { toast.error(errText(e)); }
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);
  if (!data) return null;
  const add = async (e) => {
    e.preventDefault();
    const email = val.trim().toLowerCase();
    if (!email) return;
    setBusy("add");
    try {
      await api.addSuppression(email);
      toast.success(`${email} added to the blocklist — never emailed again in any campaign`);
      setVal(""); await load(); onChanged?.();
    } catch (err) { toast.error(errText(err)); } finally { setBusy(""); }
  };
  const unblock = async (email) => {
    if (!window.confirm(`Unblock ${email}? Any unsent copy comes back as an unapproved draft — nothing is sent until you approve it again.`)) return;
    setBusy(email);
    try { const r = await api.unblockEmail(email); toast.success(`${email} unblocked${r.restored ? ` · ${r.restored} contact record${r.restored === 1 ? "" : "s"} restored (unapproved)` : ""}`); await load(); onChanged?.(); }
    catch (err) { toast.error(errText(err)); } finally { setBusy(""); }
  };
  const unblockDomain = async (domain) => {
    if (!window.confirm(`Unblock the whole domain @${domain}? Future uploads from this company will be accepted again. Addresses already blocked stay blocked until you unblock them individually.`)) return;
    setBusy(domain);
    try { await api.unblockDomain(domain); toast.success(`@${domain} unblocked`); await load(); onChanged?.(); }
    catch (err) { toast.error(errText(err)); } finally { setBusy(""); }
  };
  const domains = data.domains || [];
  return (
    <section className="bg-white border border-[#E8E1D2]" data-testid="blocklist-panel">
      <header className="px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-2">
        <Ban size={15} className="text-[#C84C21] shrink-0" />
        <h3 className="font-serif text-[1.1rem] text-[#0F1825]">Blocklist</h3>
        <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-[#5C6470]" data-testid="blocklist-count">
          {data.total} address{data.total === 1 ? "" : "es"}{domains.length ? <span className="text-[#C84C21]"> · {domains.length} whole domain{domains.length === 1 ? "" : "s"}</span> : null} · blocked in every campaign, now and later
        </span>
        {!readOnly && (
          <form onSubmit={add} className="ml-auto flex items-center gap-2">
            <input value={val} onChange={(e) => setVal(e.target.value)} placeholder="name@company.com" className={`${inputCls} !py-1.5 !text-[12.5px] w-[260px]`} data-testid="suppression-input" />
            <button type="submit" disabled={!!busy || !val.trim()} className={btnLight} data-testid="suppression-add">
              {busy === "add" ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} Block address
            </button>
          </form>
        )}
      </header>
      <p className="px-4 pb-3 text-[12.5px] text-[#5C6470] leading-relaxed">
        Who ends up here: contacts you <strong className="text-[#0F1825]">delete</strong> (deleting a whole company also blocks its domain, shown in red), anyone whose reply says <strong className="text-[#0F1825]">unsubscribe / remove me</strong> (added automatically on every sync), the <strong className="text-[#0F1825]">DNC</strong> button, and <strong className="text-[#0F1825]">bounced</strong> addresses you delete. Unblocking restores records as <em>unapproved</em> drafts — nothing sends without a fresh approval.
      </p>
      {domains.length > 0 && (
        <div className="mx-4 mb-3 border border-[#C84C21]/50 bg-[#FDECE6]" data-testid="blocklist-domains">
          <div className="px-3 pt-2 font-mono text-[10px] uppercase tracking-[0.18em] text-[#C84C21] inline-flex items-center gap-1.5"><Globe size={12} /> Whole domains blocked · {domains.length}</div>
          <ul>
            {domains.map((d) => (
              <li key={d.domain} className="px-3 py-1.5 flex flex-wrap items-center gap-x-3 text-[12.5px]" data-testid={`blocklist-domain-${d.domain}`}>
                <span className="font-mono text-[12px] text-[#C84C21] font-medium">@{d.domain}</span>
                <span className="text-[#5C6470]">{d.company || ""}{d.reason ? ` · ${d.reason}` : ""}</span>
                <span className="ml-auto font-mono text-[10px] text-[#A09680]">{d.added_at ? fmtDateTime(d.added_at) : ""}{d.by ? ` · ${d.by}` : ""}</span>
                {!readOnly && (
                  <button type="button" disabled={!!busy} onClick={() => unblockDomain(d.domain)} className="font-mono text-[10px] uppercase tracking-[0.16em] text-[#C84C21] hover:text-[#0F1825] inline-flex items-center gap-1" data-testid={`unblock-domain-${d.domain}`}>
                    {busy === d.domain ? <Loader2 size={11} className="animate-spin" /> : <X size={11} />} Unblock domain
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
      {GROUPS.map((g) => <Group key={g.key} g={g} rows={data.groups?.[g.key] || []} busy={busy} onUnblock={unblock} readOnly={readOnly} />)}
      {data.total === 0 && domains.length === 0 && <div className="px-4 pb-3 text-[12px] text-[#A09680]" data-testid="blocklist-empty">Nothing blocked yet.</div>}
    </section>
  );
}
